import { refreshToken, logout } from './auth';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  let res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });

  // If 401, try refreshing the token once.
  if (res.status === 401) {
    const refreshed = await refreshToken();
    if (refreshed) {
      res = await fetch(`${API_BASE}${path}`, { ...init, headers, credentials: 'include' });
    }
    if (res.status === 401) {
      throw new Error('unauthorized');
    }
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  me: () => request<{ user: { id: string; email: string; inbox_prefix: string } }>('/api/auth/me'),
  listReceipts: () => request<{ receipts: Receipt[] }>('/api/receipts'),
  getReceipt: (id: string) => request<{ receipt: Receipt }>(`/api/receipts/${id}`),
  uploadReceipt: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return request<{ receipt: Receipt }>('/api/receipts/upload', { method: 'POST', body: fd });
  },
  confirmReceipt: (id: string, patch: Partial<Receipt>) =>
    request<{ receipt: Receipt }>(`/api/receipts/${id}/confirm`, { method: 'POST', body: JSON.stringify(patch) }),
  postReceipt: (id: string) =>
    request<{ ok: true; xero_transaction_id: string }>(`/api/receipts/${id}/post`, { method: 'POST' }),
  retryReceipt: (id: string) =>
    request<{ ok: true }>(`/api/receipts/${id}/retry`, { method: 'POST' }),
  deleteReceipt: (id: string) =>
    request<{ ok: true }>(`/api/receipts/${id}`, { method: 'DELETE' }),
  removeFromXero: (id: string) =>
    request<{ ok: true }>(`/api/receipts/${id}/remove-from-xero`, { method: 'POST' }),
  listPostedReceipts: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return request<{ receipts: Receipt[] }>(`/api/receipts/posted?${params.toString()}`);
  },
  bulkUpdateAccounts: (updates: Array<{ id: string; account_code: string }>) =>
    request<{ results: Array<{ id: string; ok: boolean; error?: string }> }>(
      '/api/receipts/bulk-update-accounts',
      { method: 'POST', body: JSON.stringify({ updates }) }
    ),
  xeroStatus: () =>
    request<{ connected: boolean; connection: { default_bank_account_id: string | null; default_account_code: string | null } | null }>(
      '/api/xero/status'
    ),
  xeroDisconnect: () => request<{ ok: true }>('/api/xero/disconnect', { method: 'DELETE' }),
  xeroBankAccounts: () =>
    request<{ accounts: Array<{ AccountID: string; Code: string; Name: string }> }>('/api/xero/bank-accounts'),
  xeroAccounts: () =>
    request<{ accounts: Array<{ AccountID: string; Code: string; Name: string; Type: string }> }>('/api/xero/accounts'),
  saveXeroDefaults: (body: { default_bank_account_id?: string; default_account_code?: string }) =>
    request<{ ok: true }>('/api/settings/xero-defaults', { method: 'PUT', body: JSON.stringify(body) }),
  listCategoryRules: () =>
    request<{ rules: Array<{ id: string; vendor_pattern: string; account_code: string }> }>('/api/settings/category-rules'),
  createCategoryRule: (body: { vendor_pattern: string; account_code: string }) =>
    request<{ id: string }>('/api/settings/category-rules', { method: 'POST', body: JSON.stringify(body) }),
  deleteCategoryRule: (id: string) =>
    request<{ ok: true }>(`/api/settings/category-rules/${id}`, { method: 'DELETE' }),
  listEmailAliases: () =>
    request<{ aliases: Array<{ id: string; email: string }> }>('/api/settings/email-aliases'),
  addEmailAlias: (email: string) =>
    request<{ id: string }>('/api/settings/email-aliases', { method: 'POST', body: JSON.stringify({ email }) }),
  deleteEmailAlias: (id: string) =>
    request<{ ok: true }>(`/api/settings/email-aliases/${id}`, { method: 'DELETE' }),
  billingStatus: () =>
    request<{
      plan: 'free' | 'solo' | 'business' | 'pro';
      status?: string;
      current_period_end?: number;
      cancel_at_period_end?: boolean;
      usage: { lifetime: number; period: number; cap: number; overage: number };
    }>('/api/billing/status'),
  billingCheckout: (plan: 'solo' | 'business' | 'pro', promoCode?: string) =>
    request<{ url: string }>('/api/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, promoCode }),
    }),
  billingPortal: () =>
    request<{ url: string }>('/api/billing/portal', { method: 'POST' }),
  billingPromoPreview: (code: string) =>
    request<{ valid: boolean; discount?: string; id?: string }>(
      '/api/billing/promo-preview',
      { method: 'POST', body: JSON.stringify({ code }) }
    ),
  gmailStatus: () =>
    request<{ connected: boolean; email?: string; last_synced_at?: number; enabled?: boolean }>('/api/gmail/status'),
  gmailDisconnect: () =>
    request<{ ok: true }>('/api/gmail/disconnect', { method: 'DELETE' }),
  myobStatus: () =>
    request<{
      configured: boolean;
      connected: boolean;
      connection: null | {
        company_file_id: string;
        company_file_name: string | null;
        product_version: string | null;
        default_account_uid: string | null;
        default_bank_account_uid: string | null;
        default_tax_code_uid: string | null;
        refresh_failed_at: number | null;
      };
    }>('/api/myob/status'),
  myobDisconnect: () => request<{ ok: true }>('/api/myob/disconnect', { method: 'DELETE' }),
  myobCompanyFiles: () =>
    request<{ files: Array<{ Id: string; Name: string; Uri: string; ProductVersion?: string; ProductLevel?: { Code: string; Name: string }; Country?: string }> }>(
      '/api/myob/company-files'
    ),
  myobSelectCompanyFile: (company_file_id: string) =>
    request<{ ok: true }>('/api/myob/company-file', { method: 'PUT', body: JSON.stringify({ company_file_id }) }),
  myobBankAccounts: () =>
    request<{ accounts: Array<{ UID: string; DisplayID: string; Name: string; Type: string; BSBNumber?: string; AccountNumber?: string }> }>(
      '/api/myob/bank-accounts'
    ),
  myobAccounts: () =>
    request<{ accounts: Array<{ UID: string; DisplayID: string; Name: string; Type: string; Description?: string }> }>('/api/myob/accounts'),
  myobTaxCodes: () =>
    request<{ codes: Array<{ UID: string; Code: string; Description: string; Rate: number; Type: string }> }>('/api/myob/tax-codes'),
  saveMyobDefaults: (body: { default_bank_account_uid?: string; default_account_uid?: string; default_tax_code_uid?: string }) =>
    request<{ ok: true }>('/api/settings/myob-defaults', { method: 'PUT', body: JSON.stringify(body) }),
  myobRefreshAccounts: () =>
    request<{ ok: true }>('/api/settings/myob-refresh-accounts', { method: 'POST' }),
  categorisationMetrics: () =>
    request<{
      window_days: number;
      total: number;
      unedited: number;
      accuracy: number | null;
      needs_review: number;
      sources: { rule: number; llm: number; corpus: number; fuzzy: number };
      top_edited: Array<{ vendor: string; account_code: string; count: number }>;
    }>('/api/receipts/categorisation-metrics'),
  refreshCoa: () =>
    request<{ ok: true; count: number }>('/api/settings/xero-refresh-accounts', { method: 'POST' }),
};

export function gmailConnectUrl(): string {
  return `${API_BASE}/api/gmail/login`;
}

export function xeroConnectUrl(): string {
  return `${API_BASE}/api/xero/login`;
}

export function myobConnectUrl(): string {
  return `${API_BASE}/api/myob/login`;
}

export interface Receipt {
  id: string;
  user_id: string;
  vendor: string | null;
  receipt_date: string | null;
  amount: number | null;
  tax: number | null;
  currency: string;
  description: string | null;
  category: string | null;
  account_code: string | null;
  xero_transaction_id: string | null;
  external_transaction_id: string | null;
  external_contact_id: string | null;
  posted_provider: 'xero' | 'myob' | 'quickbooks' | null;
  status: 'pending' | 'parsed' | 'needs_review' | 'confirmed' | 'posted' | 'reconciled' | 'failed' | 'duplicate';
  confidence: number | null;
  error_message: string | null;
  raw_parse_json: string | null;
  created_at: string;
  updated_at: string;
}

export interface ParsedReceiptExtras {
  account_code_alternate: string | null;
  category_intent: string | null;
  reasoning: string | null;
}

export function parseReceiptExtras(receipt: Receipt): ParsedReceiptExtras {
  if (!receipt.raw_parse_json) return { account_code_alternate: null, category_intent: null, reasoning: null };
  try {
    const raw = JSON.parse(receipt.raw_parse_json) as Partial<ParsedReceiptExtras>;
    return {
      account_code_alternate: raw.account_code_alternate ?? null,
      category_intent: raw.category_intent ?? null,
      reasoning: raw.reasoning ?? null,
    };
  } catch {
    return { account_code_alternate: null, category_intent: null, reasoning: null };
  }
}
